<!--
 Refactor request: the button is specified here with `flex`.  This should NOT be the case, it should only be flex
 when the slot requires it.  Because it is always flex, we need the surrounding `div` to ensure wrapping flex items
 do not cause conflicts.
-->

<template>
  <component
    :is="componentType"
    :url="url"
    :class="[
      'group button place-items-center leading-none justify-center',
      {
        'inline-flex': !fullWidth,
        'flex w-full': fullWidth,
        'h-12': buttonType !== 'lid-primary' && buttonType !== 'lid-secondary' && buttonType !== 'lid-tertiary' && buttonType !== 'lid-read-more',
        'md:justify-start': !centerContent,
        'bg-app-lime text-black font-semibold rounded-xl border-b-2 uppercase px-4 u-box-shadow': buttonType === 'primary',
        'bg-app-lime text-black font-semibold rounded-sm shadow-sm uppercase px-4': buttonType === 'primary-flat',
        'bg-black text-white font-semibold rounded-xl border-b-2 uppercase px-4 u-box-shadow--white': buttonType === 'secondary',
        'bg-black text-white font-semibold rounded-r-xl border-b-2 uppercase px-4 u-box-shadow--white': buttonType === 'secondary-flat-left',
        'bg-app-green text-black font-semibold rounded-xl justify-center px-4 border-b-none u-box-shadow--white': buttonType === 'tertiary',
        'bg-white text-black font-semibold rounded-xl justify-center border-b-2 px-1 text-center md:px-4 u-box-shadow': buttonType === 'quaternary',
        'bg-black text-white font-semibold rounded-xl justify-center uppercase px-4 border-b-2 u-box-shadow--white': buttonType === 'quinary',
        'bg-app-green text-white font-bold rounded-xl border-b-2 uppercase px-4 h-10 justify-center border-b-2 border-white border-solid u-box-shadow--white':
          buttonType === 'filter',
        'bg-app-lid--blue-smalt text-white font-black lg:text-lg xl:text-2xl rounded-full justify-center py-2 px-4 sm:p-3 md:px-6 md:py-4 xl:px-8 2xl:px-10 2xl:py-6 transform transition ease-in-out duration-200 hover:bg-app-lid--blue-prussian hover:text-white':
          buttonType === 'lid-primary',
        'bg-white text-app-lid--blue-smalt font-black lg:text-lg xl:text-2xl rounded-full justify-center p-2 sm:p-3 md:px-6 md:py-4 xl:px-8 2xl:px-10 2xl:py-6 transform transition ease-in-out duration-200 hover:bg-app-lid--blue-smalt hover:text-white':
          buttonType === 'lid-secondary',
        'bg-white text-app-lid--blue-prussian font-black lg:text-lg xl:text-2xl rounded-full justify-center p-2 sm:p-3 md:px-6 md:py-4 xl:px-8 2xl:px-10 2xl:py-6 transform transition ease-in-out duration-200 hover:bg-app-lid--yellow-casablanca':
          buttonType === 'lid-tertiary',
        'sm:text-lg xl:text-2xl text-app-lid--blue-prussian font-black pb-2 xl:pb-3 transform transition ease-in-out duration-200 border-white border-solid border-b-2 hover:border-app-lid--blue-prussian':
          buttonType === 'lid-read-more',
      },
    ]"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'

const props = defineProps({
  url: { type: [String, Object] as PropType<string | Record<string, any>>, required: false, default: undefined },
  fullWidth: { type: Boolean, required: false, default: false },
  centerContent: { type: Boolean, required: false, default: undefined },
  buttonType: {
    type: String as PropType<string>,
    required: true,
    validator: (value: string) =>
      [
        'primary',
        'primary-flat',
        'secondary',
        'secondary-flat-left',
        'tertiary',
        'quaternary',
        'quinary',
        'filter',
        'lid-primary',
        'lid-secondary',
        'lid-tertiary',
        'lid-read-more',
      ].includes(value),
  },
})

const AtomLink = resolveComponent('AtomLink')
const componentType = props.url ? AtomLink : 'button'
</script>

<style lang="scss" scoped>
@import 'assets/scss/mixins/box-shadow';

@mixin box-shadow($color: theme('colors.app.green-100')) {
  @apply transform transition ease-in-out duration-200;
  box-shadow: 0 2px 0 theme('colors.app.green-100');
  &:hover {
    box-shadow: 0 -2px 0 theme('colors.app.green-100');
  }
}

.u-box-shadow {
  @include box-shadow;
}
.u-box-shadow--white {
  @include box-shadow(theme('colors.white'));
}
</style>
